import React, { useCallback, useLayoutEffect, useRef } from "react";
import artistGif from "../../../assets/images/artist.gif";

const NAME_FONT_MIN_REM = 0.75;

function remToPx(rem) {
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return rem * root;
}

function fitNameToLine(nameEl, minPx) {
  nameEl.style.fontSize = "";
  const maxSize = parseFloat(getComputedStyle(nameEl).fontSize);
  if (nameEl.scrollWidth <= nameEl.clientWidth) {
    return;
  }
  let lo = minPx;
  let hi = maxSize;
  let best = minPx;
  while (lo <= hi + 0.25) {
    const mid = (lo + hi) / 2;
    nameEl.style.fontSize = `${mid}px`;
    if (nameEl.scrollWidth <= nameEl.clientWidth) {
      best = mid;
      lo = mid + 0.25;
    } else {
      hi = mid - 0.25;
    }
  }
  nameEl.style.fontSize = `${best}px`;
}

/**
 * Shared artist card (bar-card style) for Management > Artists,
 * Artist > My Artists, Find Covers, Find Artists.
 * Shows photo (or placeholder), stage name, and optional Pending badge.
 */
const BarOwnerArtistCard = ({ artist, onClick, hasPending = false }) => {
  const contentRef = useRef(null);
  const nameRef = useRef(null);
  const photoUrl = artist?.artist_pic || artistGif;
  const name = artist?.stage_name || "Artist";

  const applyFit = useCallback(() => {
    const nameEl = nameRef.current;
    if (!nameEl) return;
    fitNameToLine(nameEl, remToPx(NAME_FONT_MIN_REM));
  }, []);

  useLayoutEffect(() => {
    applyFit();
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      applyFit();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [name, applyFit]);

  return (
    <div
      className="bar-card bar-card--artist"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-label={`View ${name}`}
    >
      <div className="bar-card-photos" style={{ position: "relative" }}>
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name}
            className="bar-card-photo"
            onError={(e) => {
              e.currentTarget.src = artistGif;
            }}
          />
        ) : (
          <div className="bar-card-photo-placeholder">🎤</div>
        )}
        {hasPending && (
          <span
            className="bar-card-pending-badge"
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "var(--warning, #f59e0b)",
              color: "#fff",
              fontSize: "0.7rem",
              padding: "2px 6px",
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            Pending
          </span>
        )}
      </div>
      <div ref={contentRef} className="bar-card-content">
        <h3 ref={nameRef} className="bar-card-name" title={name}>
          {name}
        </h3>
      </div>
    </div>
  );
};

export default BarOwnerArtistCard;
